import React, { useEffect, useState } from 'react';
import { KeyRound, Loader2, ShieldCheck, Trash2, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { getAddressSettings, saveAddressKey, deleteAddressKey } from '../../services/api';
import { UkAddressFields } from '../../components/UkAddressFields';

export default function AddressLookupSettings() {
  const [state, setState] = useState(null);
  const [key, setKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [trial, setTrial] = useState(null);

  const load = () => getAddressSettings().then(setState).catch(() => toast.error('Could not load settings'));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const r = await saveAddressKey(key.trim());
      toast.success(`Key saved (ending …${r.last4})`);
      setKey('');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save the key');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm('Remove the saved key? Address lookup will fall back to postcode-area only.')) return;
    await deleteAddressKey();
    toast.success('Key removed');
    load();
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-3xl space-y-6" data-testid="address-lookup-settings">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Address lookup</h1>
        <p className="text-sm text-slate-500 mt-1">UK premises-level postcode lookup by Ideal Postcodes (Royal Mail PAF).</p>
      </div>

      <section className="border border-slate-200 rounded-lg p-4 space-y-4 bg-white">
        <div className="flex items-center gap-2 text-sm" data-testid="address-key-status">
          {state?.configured ? (
            <><ShieldCheck className="h-4 w-4 text-emerald-600" /> Key configured
              {state.source === 'environment' ? ' (server environment)' : ` (ending …${state.last4})`}</>
          ) : (
            <><KeyRound className="h-4 w-4 text-amber-600" /> No key — lookups show the postcode area only</>
          )}
        </div>
        {state && (
          <div className="text-xs text-slate-500" data-testid="address-usage">
            Paid lookups this month: <span className="font-medium text-slate-800">{state.lookupsThisMonth}</span>
            {' · '}Postcodes cached (free for 30 days): {state.cachedPostcodes}
          </div>
        )}
        <div className="space-y-2">
          <Label htmlFor="ideal-key">{state?.configured ? 'Replace API key' : 'API key'}</Label>
          <div className="flex gap-2">
            <Input
              id="ideal-key"
              type="password"
              autoComplete="off"
              spellCheck={false}
              value={key}
              onChange={(e) => setKey(e.target.value)}
              placeholder="ak_…"
              data-testid="address-key-input"
            />
            <Button onClick={save} disabled={busy || !key.trim()} data-testid="address-key-save-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save key'}
            </Button>
          </div>
          <p className="text-xs text-slate-500">
            The key is checked with Ideal Postcodes (free), encrypted and stored on the server only. It is never shown again or sent to browsers.
          </p>
        </div>
        {state?.source === 'saved' && (
          <Button variant="outline" size="sm" onClick={remove} className="text-red-600" data-testid="address-key-remove-btn">
            <Trash2 className="h-4 w-4 mr-1.5" /> Remove saved key
          </Button>
        )}
      </section>

      <section className="border border-slate-200 rounded-lg p-4 space-y-3 bg-white">
        <div className="flex items-center gap-2 text-sm font-medium text-slate-900"><MapPin className="h-4 w-4" /> Try a lookup</div>
        <p className="text-xs text-slate-500">Each new postcode uses 1 credit; repeat searches within 30 days are free.</p>
        <UkAddressFields value={trial} onChange={setTrial} testId="address-trial" />
      </section>
    </div>
  );
}
